import * as _ from "lodash";
import {
    defaultInitializeLocalizeInstance,
    GetLocalizeInstance,
    GetLabel
} from "@ceridianhcm/globalization";
var fp = require('lodash/fp');

/**
 * Checks if value is classified as a Number primitive or object.
 *
 * Note: To exclude Infinity, -Infinity, and NaN, which are classified as numbers, use the _.isFinite method.
 *
 * @param value The value to check.
 * @return Returns true if value is correctly classified, else false.
 */
function isNumber(value?: any): value is number {
    return _.isNumber(value);
}

/**
 * Checks if value is a finite primitive number.
 *
 * Note: This method is based on Number.isFinite.
 *
 * @param value The value to check.
 * @return Returns true if value is a finite number, else false.
 */
function isFinite(value ?: any): boolean {
    return _.isFinite(value);
}

/**
 * Checks if value is classified as an Array object.
 * @param value The value to check.
 *
 * @return Returns true if value is correctly classified, else false.
 */
function isArray(value?: any): value is any[];
/**
 * DEPRECATED
 */
function isArray<T>(value?: any): value is any[];
function isArray(value?: any) {
    return _.isArray(value);
}

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @category Lang
 * @param value The value to check.
 * @returns Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike<T>(value: T & string & number): boolean; // should only match if T = any

/**
 * @see _.isArrayLike
 */
function isArrayLike(value: ((...args: any[]) => any) | null | undefined): value is never;

/**
 * @see _.isArrayLike
 */
function isArrayLike(value: any): value is { length: number };
function isArrayLike(value: any) {
    return _.isArrayLike(value);
}

/**
* Checks if value is classified as a String primitive or object.
*
* @param value The value to check.
* @return Returns true if value is correctly classified, else false.
*/
function isString(value?: any): value is string {
    return _.isString(value);
}

/**
* Checks if value is the language type of Object. (e.g. arrays, functions, objects, regexes, new Number(0),
* and new String(''))
*
* @param value The value to check.
* @return Returns true if value is an object, else false.
*/
function isObject(value?: any): boolean {
    return _.isObject(value);
}

/**
* Checks if value is empty. A value is considered empty unless it’s an arguments object, array, string, or
* jQuery-like collection with a length greater than 0 or an object with own enumerable properties.
*
* @param value The value to inspect.
* @return Returns true if value is empty, else false.
*/
function isEmpty(value?: any): boolean {
    return _.isEmpty(value);
}

/**
* Checks if value is undefined.
*
* @param value The value to check.
* @return Returns true if value is undefined, else false.
*/
function isUndefined(value: any): value is undefined {
    return _.isUndefined(value);
}

/**
 * Checks if `value` is `null` or `undefined`.
 *
 * @category Lang
 * @param value The value to check.
 * @returns Returns `true` if `value` is nullish, else `false`.
 * @example
 *
 * _.isNil(null);
 * // => true
 *
 * _.isNil(void 0);
 * // => true
 *
 * _.isNil(NaN);
 * // => false
 */
function isNil(value: any): value is null | undefined {
    return _.isNil(value);
}


/**
 * Performs a deep comparison between two values to determine if they are
 * equivalent.
 *
 * **Note:** This method supports comparing arrays, array buffers, booleans,
 * date objects, error objects, maps, numbers, `Object` objects, regexes,
 * sets, strings, symbols, and typed arrays. `Object` objects are compared
 * by their own, not inherited, enumerable properties. Functions and DOM
 * nodes are **not** supported.
 *
 * @category Lang
 * @param value The value to compare.
 * @param other The other value to compare.
 * @returns Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'user': 'fred' };
 * var other = { 'user': 'fred' };
 *
 * _.isEqual(object, other);
 * // => true
 *
 * object === other;
 * // => false
 */
function isEqual(
    value: any,
    other: any
): boolean {
    return _.isEqual(value, other);
}


/**
 * Checks if value is classified as a Date object.
 * @param value The value to check.
 *
 * @return Returns true if value is correctly classified, else false.
 */
function isDate(value?: any): value is Date {
    return _.isDate(value);
}

/**
 * Checks if `value` is a safe integer. An integer is safe if it's an IEEE-754
 * double precision number which isn't the result of a rounded unsafe integer.
 *
 * **Note:** This method is based on [`Number.isSafeInteger`](https://mdn.io/Number/isSafeInteger).
 *
 * @category Lang
 * @param value The value to check.
 * @returns Returns `true` if `value` is a safe integer, else `false`.
 * @example
 *
 * _.isSafeInteger(3);
 * // => true
 *
 * _.isSafeInteger(Number.MIN_VALUE);
 * // => false
 *
 * _.isSafeInteger(Infinity);
 * // => false
 *
 * _.isSafeInteger('3');
 * // => false
 */
function isSafeInteger(value: any): boolean;
function isSafeInteger(value) {
    return _.isSafeInteger(value);
}

/**
 * Checks if value is a callable function.
 *
 * @param value The value to check.
 * @return Returns true if value is correctly classified, else false.
 */
function isFunction(value: any): any {
    return _.isFunction(value);
};

/**
 * Creates a shallow clone of value.
 *
 * Note: This method is loosely based on the structured clone algorithm and supports cloning arrays,
 * array buffers, booleans, date objects, maps, numbers, Object objects, regexes, sets, strings, symbols,
 * and typed arrays. The own enumerable properties of arguments objects are cloned as plain objects. An empty
 * object is returned for uncloneable values such as error objects, functions, DOM nodes, and WeakMaps.
 *
 * @param value The value to clone.
 * @return Returns the cloned value.
 */
function clone<T>(value: T): T {
    return _.clone(value);
}

function fpClone<T>(value: T): T;
function fpClone(...args) {
    return fp.clone(...args);
}

/**
 * This method is like _.clone except that it recursively clones value.
 *
 * @param value The value to recursively clone.
 * @return Returns the deep cloned value.
 */
function cloneDeep<T>(value: T): T {
    return _.cloneDeep(value);
}

/**
 * This method is like _.cloneDeep except that it accepts customizer which is invoked to produce the cloned value.
 * If customizer returns undefined, cloning is handled by the method instead.
 *
 * @param value The value to recursively clone.
 * @param customizer The function to customize cloning.
 * @return Returns the deep cloned value.
 */
function cloneDeepWith<T>(value: T, customizer: (value: any, key: number | string | undefined, object: any) => any): T {
    return _.cloneDeepWith(value, customizer);
}

/**
 * Performs a partial comparison between two values to determine if they are
 * equivalent.
 *
 * @see _.isMatch * 
 * @category Lang
 * @param value The value to compare.
 * @param other The other value to compare.
 * @returns Returns `true` if the values are partial matches, else `false`.
 * @example
 *
 * var object = { 'user': 'fred', name:'astair' };
 * var other = { 'user': 'fred' };
 *
 * _.isMatch(object, other);
 * // => true
 * 
 *_.isEqual(object, other);
 * // -> false
 * object === other;
 * // => false
 */
function isMatch(object: object, source: object): boolean {
    return _.isMatch(object, source);
}

/**
 * This method is like `isMatch` except that it accepts `customizer` which
 * is invoked to compare values. If `customizer` returns `undefined`, comparisons
 * are handled by the method instead. The `customizer` is invoked with five
 * arguments: (objValue, srcValue, index|key, object, source).
 *
 * @see _.isMatchWith *
 * @category Lang
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 * @example
 *
 * function isGreeting(value) {
 *   return /^h(?:i|ello)$/.test(value)
 * }
 *
 * function customizer(objValue, srcValue) {
 *   if (isGreeting(objValue) && isGreeting(srcValue)) {
 *     return true
 *   }
 * }
 *
 * const object = { 'greeting': 'hello' }
 * const source = { 'greeting': 'hi' }
 *
 * isMatchWith(object, source, customizer)
 * // => true
 */
function isMatchWith(object: object, source: object, customizer: _.isMatchWithCustomizer): boolean {
    return _.isMatchWith(object, source, customizer);
}

/**
 * Converts value to an array.
 *
 * @param value The value to convert.
 * @return Returns the converted array.
 */
function toArray<T>(value: any): T[]{
    return _.toArray(value);
}

/**
 * Checks if value is NaN.
 *
 * Note: This method is not the same as isNaN which returns true for undefined and other non-numeric values.
 *
 * @param value The value to check.
 * @return Returns true if value is NaN, else false.
 */
function isNaN(value?: any): boolean {
    return _.isNaN(value);
};

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform an dictionary value into a boolean indicating whether or not the dictionary has at least one key.
 *
 * Example: <div data-dojo-props="visible: at('rel:', 'dict').transform(transformers.hasKeys)" ...>
 */
function hasKeys(source?: any) {
    for (var prop in source) {
        if (source.hasOwnProperty(prop))
            return true;
    }
    return false;
}

//TODO
//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform a true to a false, and vice versa.
 */
function booleanInvert() {
    // format: booleanNegator
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Return true if the specified string is not empty (null, undefined or zero-length)
 */
function isNotEmpty(value?: string) {
    return isString(value) && !!(value && value.length > 0);
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Returns true if an object is not null and false otherwise
 */
function isNotNull(value?: any) {
    return value != null;
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * The inverse of isNotNull
 */
function isNull(value?: any) {
    return value == null;
}

// Globalization message when running unit tests returns this message: Cannot retrieve label lblYes. The PXT Globalization module is not initialized and labels are not yet available, generally this means a setup or dependency file has not run or finished executing yet.
//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Convert a boolean to a Yes/No string.
 */
function toYesNo(value?: boolean) {
    if (value) {
        return GetLabel('lblYes');
    } else {
        return GetLabel('lblNo');
    }
}

//TODO - Unit tests fails when using defaultInitializeLocalizeInstance()
// This function is also in globalization repo
//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 *  Format the currency specific to the users locale
 */
function formatCurrency(value?: number) {
    defaultInitializeLocalizeInstance().then(() => {
        const LocalizeInstance = GetLocalizeInstance();
        return LocalizeInstance.formatCurrency(value);
    })
}

export const LangUtil = {
    isNumber,
    isFinite,
    isArray,
    isArrayLike,
    isString,
    isObject,
    isEmpty,
    isUndefined,
    isNil,
    isEqual,
    isMatch,
    isMatchWith,
    isDate,
    isSafeInteger,
    isFunction,
    clone,
    fpClone,
    cloneDeep,
    cloneDeepWith,
    toArray,
    isNaN,
    hasKeys,
    booleanInvert,
    isNotEmpty,
    isNotNull,
    isNull,
    toYesNo,
    // formatCurrency
}

export default LangUtil;